// Stripe
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useDataMutation } from "@Libs/DataLayer/hooks/useDataMutation";
import { PaymentMethodType } from "@modules/PaymentProcessing/types";
import { usePayFlow } from "@modules/PaymentProcessing/hooks/usePayFlow";
import { usePaymentSetup } from "@modules/PaymentProcessing/hooks/usePaymentSetup";
import { useStripeError } from "./useStripeError";
import { useEffect } from "react";

interface Props {
  name: string;
  onSuccess: () => void;
  onError: () => void;
  onSetupError: () => void;
  enabled?: boolean;
  paymentMethodId?: string;
}

export const usePaymentIntent = ({
  name,
  onSuccess,
  onError,
  onSetupError,
  enabled,
  paymentMethodId,
}: Props) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const {
    state: { resourceId, resourceType, relatedServiceId },
  } = usePayFlow();
  const { stripeError, setStripeError } = useStripeError();

  const { setupConfig, isLoading, invalidate, isError } = usePaymentSetup({
    paymentMethod: PaymentMethodType.CREDIT_CARD,
    paymentProvider: "stripe",
    resourceId,
    resourceType,
    enabled,
    onError: onSetupError,
    relatedServiceId,
  });

  const stripe = useStripe();
  const elements = useElements();

  const pay = async () => {
    return await stripe.confirmCardPayment(setupConfig.clientSecret as string, {
      payment_method: paymentMethodId
        ? paymentMethodId
        : {
            card: elements.getElement(CardElement),
            billing_details: {
              name,
            },
          },
    });
  };

  const submit = useDataMutation(async () => {
    try {
      const result = await pay();
      const { error } = result;
      if (error?.type === "api_connection_error")
        throw new Error(error.message);
      if (!!error) {
        setStripeError(error);
      } else onSuccess();
    } catch (error) {
      onError();
      throw new Error(error);
    }
  });

  const cleanError = () => {
    setStripeError(undefined);
  };

  useEffect(() => {
    return () => {
      invalidate();
    };
  }, []);

  return {
    setupConfig,
    submit,
    stripeError,
    cleanError,
    setStripeError,
    isLoading,
    isSetupError: isError,
  };
};
